import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  fetchAnalyticsAiVsManual,
  fetchAnalyticsByCostCenter,
  fetchAnalyticsByResourceType,
  fetchAnalyticsByState,
  fetchAnalyticsSummary,
  fetchBudgetPeriods,
  fetchCostCenters,
} from './forms.apis';
import { ILookupItem } from './forms.models';
import {
  IAnalyticsAiVsManual,
  IAnalyticsByCostCenterItem,
  IAnalyticsByResourceTypeItem,
  IAnalyticsByStateItem,
  IAnalyticsSummary,
  IFormsAnalyticsFilters,
} from './analytics.models';

type Props = {
  navigate?: (path: string) => void;
};

type State = {
  loading: boolean;
  error: string;

  filters: IFormsAnalyticsFilters;
  periods: ILookupItem[];
  costcenters: ILookupItem[];

  summary: IAnalyticsSummary | null;
  byState: IAnalyticsByStateItem[];
  byCostCenter: IAnalyticsByCostCenterItem[];
  byResourceType: IAnalyticsByResourceTypeItem[];
  aiVsManual: IAnalyticsAiVsManual | null;
};

const DEFAULT_FILTERS: IFormsAnalyticsFilters = {
  periodix: '',
  costcenterix: '',
  fromdate: '',
  todate: '',
};

const PIE_COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f', '#0288d1'];

export default class FormsAnalyticsPage extends React.Component<any, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      error: '',

      filters: { ...DEFAULT_FILTERS },
      periods: [],
      costcenters: [],

      summary: null,
      byState: [],
      byCostCenter: [],
      byResourceType: [],
      aiVsManual: null,
    };
  }

  componentDidMount(): void {
    this.loadInitialData();
  }

  private loadInitialData = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const [periods, costcenters] = await Promise.all([
        fetchBudgetPeriods(),
        fetchCostCenters(),
      ]);

      this.setState(
        {
          periods,
          costcenters,
        },
        () => this.loadAnalytics()
      );
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load analytics lookups',
      });
    }
  };

  private loadAnalytics = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const { filters } = this.state;

      const [summary, byState, byCostCenter, byResourceType, aiVsManual] =
        await Promise.all([
          fetchAnalyticsSummary(filters),
          fetchAnalyticsByState(filters),
          fetchAnalyticsByCostCenter(filters),
          fetchAnalyticsByResourceType(filters),
          fetchAnalyticsAiVsManual(filters),
        ]);

      this.setState({
        summary,
        byState,
        byCostCenter,
        byResourceType,
        aiVsManual,
        loading: false,
      });
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load analytics',
      });
    }
  };

  private handleTextChange =
    (field: keyof IFormsAnalyticsFilters) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        filters: {
          ...prevState.filters,
          [field]: value,
        },
      }));
    };

  private handleSelectChange =
    (field: keyof IFormsAnalyticsFilters) =>
    (event: SelectChangeEvent<string>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        filters: {
          ...prevState.filters,
          [field]: value,
        },
      }));
    };

  private handleSearch = (): void => {
    this.loadAnalytics();
  };

  private handleReset = (): void => {
    this.setState(
      {
        filters: { ...DEFAULT_FILTERS },
      },
      () => this.loadAnalytics()
    );
  };

  private renderSummaryCard = (
    title: string,
    value: string | number,
    subtitle?: string
  ): React.ReactNode => {
    return (
      <Card>
        <CardContent>
          <Typography variant="caption" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {value}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </CardContent>
      </Card>
    );
  };

  private getByStateChartData(): Array<{ name: string; count: number; amount: number }> {
    return this.state.byState.map((item) => ({
      name: item.state?.recname || 'Unknown',
      count: item.count,
      amount: item.amount,
    }));
  }

  private getByCostCenterChartData(): Array<{ name: string; amount: number }> {
    return this.state.byCostCenter.map((item) => ({
      name: item.costcenter?.recname || 'Unknown',
      amount: item.amount,
    }));
  }

  private getByResourceTypeChartData(): Array<{ name: string; amount: number }> {
    return this.state.byResourceType.map((item) => ({
      name: item.resourcetype?.recname || 'Unknown',
      amount: item.amount,
    }));
  }

  private getAiVsManualChartData(): Array<{ name: string; count: number; amount: number }> {
    const { aiVsManual } = this.state;

    if (!aiVsManual) {
      return [];
    }

    return [
      {
        name: 'AI Assisted',
        count: aiVsManual.ai.count,
        amount: aiVsManual.ai.amount,
      },
      {
        name: 'Manual',
        count: aiVsManual.manual.count,
        amount: aiVsManual.manual.amount,
      },
    ];
  }

  render(): React.ReactNode {
    const {
      loading,
      error,
      filters,
      periods,
      costcenters,
      summary,
      byCostCenter,
      byResourceType,
    } = this.state;

    const byStateChartData = this.getByStateChartData();
    const byCostCenterChartData = this.getByCostCenterChartData();
    const byResourceTypeChartData = this.getByResourceTypeChartData();
    const aiVsManualChartData = this.getAiVsManualChartData();

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Forms Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Operational and management insight for requisitions, reviews, and approvals.
            </Typography>
          </Box>
        </Stack>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel id="analytics-period-label">Budget Period</InputLabel>
                  <Select
                    labelId="analytics-period-label"
                    value={filters.periodix}
                    label="Budget Period"
                    onChange={this.handleSelectChange('periodix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {periods.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel id="analytics-cc-label">Cost Center</InputLabel>
                  <Select
                    labelId="analytics-cc-label"
                    value={filters.costcenterix}
                    label="Cost Center"
                    onChange={this.handleSelectChange('costcenterix')}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {costcenters.map((item) => (
                      <MenuItem key={item.recix} value={item.recix}>
                        {item.recname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  value={filters.fromdate}
                  onChange={this.handleTextChange('fromdate')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  value={filters.todate}
                  onChange={this.handleTextChange('todate')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={this.handleSearch}>
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleReset}
                  >
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading && !summary ? (
          <Card>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={24} />
                <Typography>Loading analytics...</Typography>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                {this.renderSummaryCard('Draft Count', summary?.draftcount || 0)}
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                {this.renderSummaryCard('Submitted Count', summary?.submittedcount || 0)}
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                {this.renderSummaryCard('Under Review', summary?.reviewcount || 0)}
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                {this.renderSummaryCard('Approved Count', summary?.approvedcount || 0)}
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                {this.renderSummaryCard(
                  'Requested Total',
                  Number(summary?.requestedtotal || 0).toLocaleString()
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                {this.renderSummaryCard(
                  'Approved Total',
                  Number(summary?.approvedtotal || 0).toLocaleString()
                )}
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Requisitions by State
                    </Typography>
                    <Box sx={{ width: '100%', height: 320 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={byStateChartData}
                            dataKey="count"
                            nameKey="name"
                            outerRadius={100}
                            label
                          >
                            {byStateChartData.map((entry, index) => (
                              <Cell
                                key={`cell-state-${index}`}
                                fill={PIE_COLORS[index % PIE_COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      AI vs Manual Capture
                    </Typography>
                    <Box sx={{ width: '100%', height: 320 }}>
                      <ResponsiveContainer>
                        <BarChart data={aiVsManualChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="count" fill="#1976d2" name="Count" />
                          <Bar dataKey="amount" fill="#2e7d32" name="Amount" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Requested Amount by Cost Center
                    </Typography>
                    <Box sx={{ width: '100%', height: 340 }}>
                      <ResponsiveContainer>
                        <BarChart data={byCostCenterChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="amount" fill="#ed6c02" name="Amount" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Requested Amount by Resource Type
                    </Typography>
                    <Box sx={{ width: '100%', height: 340 }}>
                      <ResponsiveContainer>
                        <BarChart data={byResourceTypeChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="amount" fill="#9c27b0" name="Amount" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top Cost Centers
                    </Typography>

                    <Stack spacing={1}>
                      {byCostCenter.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No data available.
                        </Typography>
                      ) : (
                        byCostCenter.slice(0, 10).map((item, index) => (
                          <Stack
                            key={`${item.costcenter?.recix || index}`}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                              py: 1,
                              borderBottom:
                                index === byCostCenter.slice(0, 10).length - 1
                                  ? 'none'
                                  : '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography variant="body2">
                              {item.costcenter?.recname || 'Unknown'}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                              {Number(item.amount || 0).toLocaleString()}
                            </Typography>
                          </Stack>
                        ))
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    );
  }
}